import { useQuery, useQueryClient } from '@tanstack/react-query'
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore'
import { db } from '@/core/firebase/client'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { cancelOnlineBooking, isBookingError } from '@/features/lugares/api/booking-api'

/**
 * «Mis reservas» — the player's court bookings, read from THIS project.
 *
 * The row is a mirror: `confirm_booking` in clubmanager writes a thin copy into
 * `users/{uid}/courtBookings/{id}` on the already-granted direction, so the
 * player app never needs clubmanager credentials to show what somebody booked.
 * clubmanager stays authoritative; this is a projection, and the rules make
 * that stick (write-locked to clients).
 *
 * Background reconciliation can now create/update a copy after the original
 * response. Refresh while this screen is visible, so that repair appears
 * without requiring another booking action or a manual page reload.
 */

export interface CourtBooking {
  id: string
  clubManagerId: string
  courtName: string
  startMs: number
  durationMinutes: number
  status: 'confirmed' | 'cancelled'
}

function parse(id: string, raw: Record<string, unknown>): CourtBooking | null {
  const startMs = raw['startMs']
  // A row we cannot place in time is not renderable — a card reading
  // "Invalid Date" is worse than a row that never appears, and this is a
  // mirror, so the real booking is still safe in the club's calendar.
  if (typeof startMs !== 'number' || !Number.isFinite(startMs)) return null
  return {
    id,
    clubManagerId: typeof raw['clubManagerId'] === 'string' ? raw['clubManagerId'] : '',
    courtName: typeof raw['courtName'] === 'string' ? raw['courtName'] : 'Pista',
    startMs,
    durationMinutes:
      typeof raw['durationMinutes'] === 'number' && raw['durationMinutes'] > 0
        ? raw['durationMinutes']
        : 90,
    status: raw['status'] === 'cancelled' ? 'cancelled' : 'confirmed',
  }
}

export function useCourtBookings() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['court-bookings', uid],
    // Never under the bypass: the mock fixtures have no booking mirror, and
    // inventing one would make the design walk exercise a path production
    // cannot reach.
    enabled: !AUTH_BYPASS && uid !== null,
    staleTime: 30_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<CourtBooking[]> => {
      const snap = await getDocs(
        query(collection(db, 'users', uid!, 'courtBookings'),
          where('startMs', '>', Date.now()), orderBy('startMs', 'desc')),
      )
      return snap.docs
        .map((d) => parse(d.id, d.data() as Record<string, unknown>))
        .filter((b): b is CourtBooking => b !== null)
    },
  })
}

/**
 * Cancel through the CLUB, then refresh.
 *
 * Deliberately not an optimistic update. The authoritative booking lives in the
 * other project, and showing "cancelada" before that write lands would tell a
 * player their court is released when it may not be — they stop planning around
 * it, the club still holds the slot, and nobody finds out until somebody turns
 * up. The round trip is a second; the wrong answer lasts until the club rings.
 */
export function useCancelCourtBooking() {
  const qc = useQueryClient()
  return async (booking: CourtBooking): Promise<{ ok: true } | { ok: false; message: string }> => {
    const res = await cancelOnlineBooking({
      clubId: booking.clubManagerId,
      bookingId: booking.id,
    })
    if (isBookingError(res)) return { ok: false, message: res.message }
    // Both: the booking list, and the club's grid, where that hour is now free
    // again for whoever looks next.
    await qc.invalidateQueries({ queryKey: ['court-bookings'] })
    void qc.invalidateQueries({ queryKey: ['club-availability'] })
    return { ok: true }
  }
}
